import { chatModeSchema } from '../chat-events'
import { oc } from '@orpc/contract'
import { z } from 'zod'

export const chatThreadSchema = z.object({
  /** Set for a document's bubble thread (PLAN-079). */
  documentId: z.string().nullable().optional(),
  /** Set for an inbox email's thread (PLAN-084). */
  inboxEmailId: z.string().nullable().optional(),
  id: z.string().min(1),
  projectId: z.string().min(1),
  title: z.string().nullable(),
  pinned: z.boolean(),
  createdBy: z
    .object({
      userId: z.string(),
      name: z.string().nullable(),
      avatarUrl: z.string().nullable(),
    })
    .nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** When the last message landed; null for threads untouched since the field was added. */
  lastMessageAt: z.string().nullable(),
  /** Board 5q82: when the thread was archived (hidden from the list); null = not archived. */
  archivedAt: z.string().nullable().optional(),
  /** Board 5q82: the project's onboarding thread. */
  onboarding: z.boolean().optional(),
})

export const chatListThreadsContract = oc
  .input(
    z.object({
      projectId: z.string().min(1),
      /** Board 5q82: also return archived threads (the "show archived" toggle). */
      includeArchived: z.boolean().optional(),
    })
  )
  .output(
    z.object({
      items: z.array(chatThreadSchema),
    })
  )

export const chatGetThreadContract = oc
  .input(
    z.object({
      projectId: z.string().min(1),
      threadId: z.string().min(1),
    })
  )
  .output(z.object({ thread: chatThreadSchema }))

export const chatCreateThreadContract = oc
  .input(
    z.object({
      projectId: z.string().min(1),
      title: z.string().optional(),
    })
  )
  .output(chatThreadSchema)

/**
 * The chat thread bound to a document (the HUB bubble, PLAN-079 §4.2):
 * created on first use, hidden from the general thread list.
 */
export const chatGetDocumentThreadContract = oc
  .input(z.object({ projectId: z.string().min(1), documentId: z.string().min(1) }))
  .output(chatThreadSchema)

/**
 * The chat thread bound to an inbox email (PLAN-084): shared by the HUB
 * bubble and the email's runs, created on first use, hidden from the list.
 */
export const chatGetInboxEmailThreadContract = oc
  .input(z.object({ projectId: z.string().min(1), emailId: z.string().min(1) }))
  .output(chatThreadSchema)

export const chatUpdateThreadContract = oc
  .input(
    z.object({
      projectId: z.string().min(1),
      threadId: z.string().min(1),
      title: z.string().min(1).optional(),
      pinned: z.boolean().optional(),
      /** Board 5q82: archive (hide from the list) or restore the thread. */
      archived: z.boolean().optional(),
    })
  )
  .output(chatThreadSchema)

export const chatDeleteThreadContract = oc
  .input(
    z.object({
      projectId: z.string().min(1),
      threadId: z.string().min(1),
    })
  )
  .output(z.object({ success: z.boolean() }))

export const chatAttachmentSchema = z.object({
  id: z.string().min(1),
  filename: z.string().min(1),
  url: z.string().min(1),
  size: z.number(),
  contentType: z.string(),
  /** When set, this attachment is a project document — agent reads via Y.Doc/readContent */
  documentId: z.string().optional(),
})

/**
 * Dev-only response modes (super-admin + non-production): make the next
 * reply fail in a specific way so the chat UI's error states are testable.
 */
export const simulateErrorKindSchema = z.enum([
  'provider_capability',
  'provider_transient',
  'hard_error',
  'unknown',
  /** A tool call fails but the turn completes with a short reply. */
  'tool_error',
])
export type SimulateErrorKind = z.infer<typeof simulateErrorKindSchema>

/** Soft = the author hides their own message (placeholder stays); hard = drop the row (retry). */
export const chatDeleteMessageContract = oc
  .input(
    z.object({
      projectId: z.string().min(1),
      threadId: z.string().min(1),
      messageId: z.string().min(1),
      mode: z.enum(['soft', 'hard']),
    })
  )
  .output(z.object({ ok: z.literal(true) }))

/** Reset a thread's agent status after a failed turn so the composer unlocks. */
export const chatClearAgentErrorContract = oc
  .input(z.object({ projectId: z.string().min(1), threadId: z.string().min(1) }))
  .output(z.object({ ok: z.literal(true) }))

/**
 * Stop the reply in progress: the store settles the assistant message as
 * `stopped` (partial output kept) for every subscriber, then the thread's
 * agent instance is asked to abort durably. `stopped` is false when the
 * thread was not busy (already finished, or stopped by someone else).
 */
export const chatStopAgentContract = oc
  .input(z.object({ projectId: z.string().min(1), threadId: z.string().min(1) }))
  .output(z.object({ ok: z.literal(true), stopped: z.boolean() }))

export const chatReactToMessageContract = oc
  .input(
    z.object({
      projectId: z.string().min(1),
      threadId: z.string().min(1),
      messageId: z.string().min(1),
      /** null clears the user's reaction. */
      reaction: z.enum(['up', 'down']).nullable(),
    })
  )
  .output(z.object({ ok: z.literal(true) }))

export const chatTriggerAgentContract = oc
  .input(
    z.object({
      projectId: z.string().min(1),
      threadId: z.string().min(1),
      content: z.string().min(1),
      /** Client-generated `msg_…` id so the sender can echo the message optimistically. */
      messageId: z.string().min(1).optional(),
      attachments: z.array(chatAttachmentSchema).optional(),
      /** Model mode for this turn (Normal/High/Max); default normal. */
      mode: chatModeSchema.optional(),
      /** Sent from a document's HUB bubble: the open document and the blocks the user pointed at. */
      documentContext: z
        .object({
          documentId: z.string().min(1),
          blockIds: z.array(z.string()).optional(),
        })
        .optional(),
      /**
       * Dev-only: ask the worker to short-circuit with a synthetic error of
       * the given kind. Server gates this on NODE_ENV !== 'production' AND
       * super-admin; otherwise the field is silently ignored.
       */
      simulateError: simulateErrorKindSchema.optional(),
    })
  )
  .output(
    z.object({
      assistantMessageId: z.string(),
      status: z.literal('triggered'),
    })
  )
  /**
   * The organization's credit budget (or the project's cap) is spent. The
   * composer keeps the draft and shows a notice; a reply already running is
   * never interrupted.
   */
  .errors({
    CREDITS_EXHAUSTED: {
      status: 402,
      message: 'Credits exhausted',
      data: z.object({ reason: z.enum(['org', 'project']) }),
    },
  })

/**
 * Board 5q82: open the project's onboarding conversation. Creates one chat
 * thread in which HUB takes the first turn (no user message) and asks about
 * the project; returns the existing thread when there already is one
 * (`started: false`, nothing dispatched). Owner/admin only.
 *
 * `retry: true` re-sends HUB's opening turn into the existing thread — the
 * Retry button on a failed first message (there is no user message to
 * re-send). Budget-gated like a new conversation.
 */
export const chatStartOnboardingContract = oc
  .input(z.object({ projectId: z.string().min(1), retry: z.boolean().optional() }))
  .output(z.object({ thread: chatThreadSchema, started: z.boolean() }))
  .errors({
    CREDITS_EXHAUSTED: {
      status: 402,
      message: 'Credits exhausted',
      data: z.object({ reason: z.enum(['org', 'project']) }),
    },
  })
